/**
 * imageCompressor.js
 * Compresses and resizes images before sending to AI analysis.
 * Reduces iPhone photos from ~8MB to ~100-200KB.
 */

const MAX_WIDTH = 1200;
const MAX_HEIGHT = 1200;
const JPEG_QUALITY = 0.65;

/**
 * Compress and resize an image file or base64 string.
 * @param {File|string} input - File object or base64 data URL
 * @returns {Promise<string>} - Compressed base64 string (without data: prefix)
 */
export function compressImage(input) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        let { width, height } = img;

        // Calculate new dimensions maintaining aspect ratio
        if (width > MAX_WIDTH || height > MAX_HEIGHT) {
          const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to JPEG with compression
        const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
        const base64 = dataUrl.split(',')[1];
        resolve(base64);
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error('Failed to load image'));

    if (typeof input === 'string') {
      // Already a base64 or data URL
      img.src = input.startsWith('data:') ? input : `data:image/jpeg;base64,${input}`;
    } else if (input instanceof File || input instanceof Blob) {
      const reader = new FileReader();
      reader.onloadend = () => { img.src = reader.result; };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(input);
    } else {
      reject(new Error('Invalid input type'));
    }
  });
}

/**
 * Compress image and also return a preview data URL.
 * @param {File|string} input
 * @returns {Promise<{base64: string, preview: string}>}
 */
export async function compressImageWithPreview(input) {
  const base64 = await compressImage(input);
  return {
    base64,
    preview: `data:image/jpeg;base64,${base64}`
  };
}

export default compressImage;
